import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { MapPin, Users, Edit2, Save, X, Eye, ExternalLink, Mail, Phone, ChevronDown } from 'lucide-react';

// Custom dropdown - avoids all native select CSS issues
const CouncillorPicker = ({ value, onChange, councillors }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = councillors.find(c => c.id === value);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: '6px',
          border: '1px solid #4b5563', background: '#1f2937', color: '#fff',
          fontSize: '14px', textAlign: 'left', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer'
        }}
      >
        <span>{selected ? `${selected.name} (${selected.ward || 'Unassigned'})` : '-- No councillor assigned --'}</span>
        <ChevronDown size={16} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #ccc', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: '240px',
          overflowY: 'auto', marginTop: '2px'
        }}>
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{ padding: '8px 12px', cursor: 'pointer', color: '#111', fontSize: '14px', borderBottom: '1px solid #eee' }}
            onMouseEnter={e => e.target.style.background = '#f0f0f0'}
            onMouseLeave={e => e.target.style.background = 'transparent'}
          >
            -- No councillor assigned --
          </div>
          {councillors.map(c => (
            <div
              key={c.id}
              onClick={() => { onChange(c.id); setOpen(false); }}
              style={{
                padding: '8px 12px', cursor: 'pointer', color: '#111', fontSize: '14px',
                background: c.id === value ? '#fef3c7' : 'transparent'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={e => e.currentTarget.style.background = c.id === value ? '#fef3c7' : 'transparent'}
            >
              <strong>{c.name}</strong><span style={{ color: '#666', marginLeft: '6px' }}>{c.ward || ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WardsTab = () => {
  const [wards, setWards] = useState([]);
  const [allCouncillors, setAllCouncillors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWard, setEditingWard] = useState(null);
  const [formData, setFormData] = useState({
    custom_headline: '',
    custom_description: '',
    councillor_bio_override: '',
    about_ward: '',
    priorities: '',
    office_hours: '',
    social_links: '',
    is_published: true,
    councillor_id: '',
    bento_primary_image_url: '',
    bento_secondary_image_url: '',
    bento_map_image_url: ''
  });
  const [uploadingBento, setUploadingBento] = useState({
    primary: false,
    secondary: false,
    map: false
  });
  const [bentoTouched, setBentoTouched] = useState({
    primary: false,
    secondary: false,
    map: false
  });

  useEffect(() => {
    fetchWards();
    fetchAllCouncillors();
  }, []);

  const fetchWards = async () => {
    try {
      const response = await api.get('/admin/wards');
      setWards(response.data.wards || []);
    } catch (error) {
      console.error('Error fetching wards:', error);
      toast.error('Failed to load ward data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCouncillors = async () => {
    try {
      const response = await api.get('/councillors');
      // endpoint returns a plain array
      const list = Array.isArray(response.data) ? response.data : (response.data.councillors || []);
      setAllCouncillors(list);
    } catch (error) {
      console.error('Error fetching councillors:', error);
    }
  };

  const bentoFieldMap = {
    primary: 'bento_primary_image_url',
    secondary: 'bento_secondary_image_url',
    map: 'bento_map_image_url'
  };

  const handleBentoUpload = async (type, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingBento((prev) => ({ ...prev, [type]: true }));
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      const response = await api.post('/admin/upload-image?folder=Other/Wards', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fieldName = bentoFieldMap[type];
      setFormData((prev) => ({ ...prev, [fieldName]: response.data.image_url }));
      setBentoTouched((prev) => ({ ...prev, [type]: true }));
      toast.success('Bento image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingBento((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleBentoClear = (type) => {
    const fieldName = bentoFieldMap[type];
    setFormData((prev) => ({ ...prev, [fieldName]: '' }));
    setBentoTouched((prev) => ({ ...prev, [type]: true }));
  };

  const handleEdit = (ward) => {
    setEditingWard(ward.ward_number);
    setFormData({
      custom_headline: ward.content?.custom_headline || '',
      custom_description: ward.content?.custom_description || '',
      councillor_bio_override: ward.content?.councillor_bio_override || '',
      about_ward: ward.content?.about_ward || '',
      priorities: ward.content?.priorities ? (typeof ward.content.priorities === 'string' ? ward.content.priorities : JSON.stringify(ward.content.priorities)) : '',
      office_hours: ward.content?.office_hours || '',
      social_links: ward.content?.social_links ? (typeof ward.content.social_links === 'string' ? ward.content.social_links : JSON.stringify(ward.content.social_links)) : '',
      is_published: ward.content?.is_published !== false,
      councillor_id: ward.councillor?.id || '',
      bento_primary_image_url: ward.content?.bento_primary_image_url || '',
      bento_secondary_image_url: ward.content?.bento_secondary_image_url || '',
      bento_map_image_url: ward.content?.bento_map_image_url || ''
    });
    setBentoTouched({ primary: false, secondary: false, map: false });
  };

  const handleSave = async (wardNumber) => {
    try {
      // Parse priorities and social_links if they look like JSON arrays/objects
      let priorities = formData.priorities;
      let socialLinks = formData.social_links;

      // Try to parse as JSON, if it fails treat as comma-separated
      try {
        if (formData.priorities && !formData.priorities.startsWith('[')) {
          priorities = JSON.stringify(formData.priorities.split(',').map(p => p.trim()).filter(Boolean));
        }
      } catch (e) {
        priorities = JSON.stringify(formData.priorities.split(',').map(p => p.trim()).filter(Boolean));
      }

      try {
        if (formData.social_links && formData.social_links.trim()) {
          JSON.parse(formData.social_links); // validate JSON
          socialLinks = formData.social_links;
        }
      } catch (e) {
        // Invalid JSON — just send empty rather than blocking save
        socialLinks = '';
      }

      const payload = {
        custom_headline: formData.custom_headline,
        custom_description: formData.custom_description,
        councillor_bio_override: formData.councillor_bio_override,
        about_ward: formData.about_ward,
        priorities: priorities,
        office_hours: formData.office_hours,
        social_links: socialLinks,
        is_published: formData.is_published,
        councillor_id: formData.councillor_id || null
      };

      if (bentoTouched.primary) {
        payload.bento_primary_image_url = formData.bento_primary_image_url || null;
      }

      if (bentoTouched.secondary) {
        payload.bento_secondary_image_url = formData.bento_secondary_image_url || null;
      }

      if (bentoTouched.map) {
        payload.bento_map_image_url = formData.bento_map_image_url || null;
      }

      await api.put(`/admin/wards/${wardNumber}`, payload);

      toast.success('Ward page updated!');
      setEditingWard(null);
      fetchWards();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save ward: ${detail}`);
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      custom_headline: '',
      custom_description: '',
      councillor_bio_override: '',
      about_ward: '',
      priorities: '',
      office_hours: '',
      social_links: '',
      is_published: true,
      councillor_id: '',
      bento_primary_image_url: '',
      bento_secondary_image_url: '',
      bento_map_image_url: ''
    });
    setBentoTouched({ primary: false, secondary: false, map: false });
  };

  const handleCancel = () => {
    setEditingWard(null);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading ward data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Ward Pages</h2>
          <p className="text-gray-500 text-sm mt-1">
            Customize content for each ward's public landing page
          </p>
        </div>
        <a
          href="/ward/ward-1"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <Eye className="w-4 h-4" />
          Preview Ward Pages
        </a>
      </div>

      <div className="space-y-4">
        {wards.map((ward) => {
          const isEditing = editingWard === ward.ward_number;
          
          return (
            <div 
              key={ward.ward_number}
              className={`bg-gray-900 border rounded-lg overflow-hidden ${
                isEditing ? 'border-[#C5A52A]' : 'border-gray-800'
              }`}
            >
              {/* Ward Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-800/50">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-500" />
                    <span className="font-bold text-white text-lg">
                      {ward.ward_number === 'mayor' ? '⭐ Regina / Mayor' : `Ward ${ward.ward_number}`}
                    </span>
                  </div>
                  {ward.councillor && (
                    <span className="text-gray-400">— {ward.councillor.name}</span>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Users className="w-4 h-4" />
                    {ward.subscriber_count || 0} subscribers
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <a
                    href={ward.ward_number === 'mayor' ? '/ward/mayor' : `/ward/ward-${ward.ward_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                    title="View Page"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-300" />
                  </a>
                  {!isEditing ? (
                    <button
                      onClick={() => handleEdit(ward)}
                      className="flex items-center gap-2 px-3 py-2 bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold rounded transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(ward.ward_number)}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                      <button
                        onClick={handleCancel}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Councillor Info (Read-only summary) */}
              {ward.councillor && !isEditing && (
                <div className="p-4 flex items-center gap-4 border-b border-gray-800/50">
                  {ward.councillor.image_url && (
                    <img 
                      src={ward.councillor.image_url} 
                      alt={ward.councillor.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <div className="text-sm text-gray-300">{ward.councillor.name}</div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                      {ward.councillor.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {ward.councillor.email}
                        </span>
                      )}
                      {ward.councillor.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {ward.councillor.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-mono ${
                    ward.content?.is_published !== false 
                      ? 'bg-green-900/30 text-green-400' 
                      : 'bg-red-900/30 text-red-400'
                  }`}>
                    {ward.content?.is_published !== false ? 'PUBLISHED' : 'DRAFT'}
                  </div>
                </div>
              )}

              {/* Edit Form */}
              {isEditing && (
                <div className="p-6 space-y-4">
                  {/* Published Toggle */}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_published}
                        onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                        className="w-4 h-4 rounded bg-gray-800 border-gray-700"
                      />
                      <span className="text-sm text-gray-300">Page is published and accessible</span>
                    </label>
                  </div>

                  {/* Councillor Assignment */}
                  <div className="p-4 bg-blue-900/20 border border-blue-900/50 rounded">
                    <label className="block text-sm text-blue-400 font-medium mb-2">
                      Assigned Councillor
                    </label>
                    <CouncillorPicker
                      value={formData.councillor_id}
                      onChange={(val) => setFormData({ ...formData, councillor_id: val })}
                      councillors={allCouncillors}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Changing this will reassign the councillor to this ward
                    </p>
                  </div>

                  {/* Custom Headline */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Custom Headline <span className="text-gray-600">(optional - overrides default)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.custom_headline}
                      onChange={(e) => setFormData({ ...formData, custom_headline: e.target.value })}
                      placeholder="e.g., Welcome to Ward 7 - Building Community Together"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>

                  {/* Custom Description */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Custom Description <span className="text-gray-600">(below headline)</span>
                    </label>
                    <textarea
                      value={formData.custom_description}
                      onChange={(e) => setFormData({ ...formData, custom_description: e.target.value })}
                      rows={3}
                      placeholder="Stay connected with your ward and councillor..."
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                    />
                  </div>

                  {/* About Ward Section */}
                  <div className="p-4 bg-green-900/20 border border-green-900/50 rounded">
                    <label className="block text-sm text-green-400 font-medium mb-2">
                      About Ward Content <span className="text-gray-500">(appears at bottom of page)</span>
                    </label>
                    <textarea
                      value={formData.about_ward}
                      onChange={(e) => setFormData({ ...formData, about_ward: e.target.value })}
                      rows={5}
                      placeholder="Tell visitors about this ward - neighborhoods, landmarks, history, community features..."
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                      data-testid={`ward-about-${ward.ward_number}`}
                    />
                  </div>

                  {/* Bento Image Grid */}
                  <div className="p-4 bg-[#0F0F0F] border border-gray-800 rounded">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-[#C5A52A] font-medium">About Section Images</p>
                        <p className="text-xs text-gray-500">Uploads appear in the bento grid (Urban Living / Green Spaces / Map)</p>
                      </div>
                      <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Suggested: 1400x1000px</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Primary Image */}
                      <div className="bg-gray-900 border border-gray-800 rounded p-3">
                        <p className="text-xs text-gray-300 mb-2 uppercase tracking-wider">Urban Living</p>
                        <div className="h-32 w-full bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                          {formData.bento_primary_image_url ? (
                            <img
                              src={formData.bento_primary_image_url}
                              alt="Urban Living"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-gray-500">No image uploaded</span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <label
                            htmlFor={`bento-primary-upload-${ward.ward_number}`}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs font-mono rounded cursor-pointer"
                            data-testid={`bento-primary-upload-${ward.ward_number}`}
                          >
                            {uploadingBento.primary ? 'Uploading...' : 'Upload'}
                          </label>
                          <input
                            id={`bento-primary-upload-${ward.ward_number}`}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => handleBentoUpload('primary', event.target.files[0])}
                            data-testid={`bento-primary-input-${ward.ward_number}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleBentoClear('primary')}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-mono rounded"
                            data-testid={`bento-primary-clear-${ward.ward_number}`}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* Secondary Image */}
                      <div className="bg-gray-900 border border-gray-800 rounded p-3">
                        <p className="text-xs text-gray-300 mb-2 uppercase tracking-wider">Green Spaces</p>
                        <div className="h-32 w-full bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                          {formData.bento_secondary_image_url ? (
                            <img
                              src={formData.bento_secondary_image_url}
                              alt="Green Spaces"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-gray-500">No image uploaded</span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <label
                            htmlFor={`bento-secondary-upload-${ward.ward_number}`}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs font-mono rounded cursor-pointer"
                            data-testid={`bento-secondary-upload-${ward.ward_number}`}
                          >
                            {uploadingBento.secondary ? 'Uploading...' : 'Upload'}
                          </label>
                          <input
                            id={`bento-secondary-upload-${ward.ward_number}`}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => handleBentoUpload('secondary', event.target.files[0])}
                            data-testid={`bento-secondary-input-${ward.ward_number}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleBentoClear('secondary')}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-mono rounded"
                            data-testid={`bento-secondary-clear-${ward.ward_number}`}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* Map Image */}
                      <div className="bg-gray-900 border border-gray-800 rounded p-3">
                        <p className="text-xs text-gray-300 mb-2 uppercase tracking-wider">Map Underlay</p>
                        <div className="h-32 w-full bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                          {formData.bento_map_image_url ? (
                            <img
                              src={formData.bento_map_image_url}
                              alt="Ward map"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-gray-500">No image uploaded</span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <label
                            htmlFor={`bento-map-upload-${ward.ward_number}`}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs font-mono rounded cursor-pointer"
                            data-testid={`bento-map-upload-${ward.ward_number}`}
                          >
                            {uploadingBento.map ? 'Uploading...' : 'Upload'}
                          </label>
                          <input
                            id={`bento-map-upload-${ward.ward_number}`}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => handleBentoUpload('map', event.target.files[0])}
                            data-testid={`bento-map-input-${ward.ward_number}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleBentoClear('map')}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-mono rounded"
                            data-testid={`bento-map-clear-${ward.ward_number}`}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bio Override */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Councillor Bio Override <span className="text-gray-600">(leave blank to use default)</span>
                    </label>
                    <textarea
                      value={formData.councillor_bio_override}
                      onChange={(e) => setFormData({ ...formData, councillor_bio_override: e.target.value })}
                      rows={4}
                      placeholder="Custom bio for this councillor on the ward page..."
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                      data-testid={`ward-bio-override-${ward.ward_number}`}
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Current default: {ward.councillor?.bio?.substring(0, 100)}...
                    </p>
                  </div>

                  {/* Priorities */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Councillor Priorities <span className="text-gray-600">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.priorities}
                      onChange={(e) => setFormData({ ...formData, priorities: e.target.value })}
                      placeholder="Infrastructure, Public Safety, Community Events"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>

                  {/* Office Hours */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Office Hours</label>
                    <input
                      type="text"
                      value={formData.office_hours}
                      onChange={(e) => setFormData({ ...formData, office_hours: e.target.value })}
                      placeholder="Monday - Friday, 9am - 5pm"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>

                  {/* Social Links */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Social Links <span className="text-gray-600">(JSON format)</span>
                    </label>
                    <textarea
                      value={formData.social_links}
                      onChange={(e) => setFormData({ ...formData, social_links: e.target.value })}
                      rows={3}
                      placeholder='{"twitter": "https://twitter.com/...", "facebook": "https://facebook.com/..."}'
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-bold text-gray-300 mb-2">About Ward Pages</h4>
        <ul className="text-sm text-gray-500 space-y-1">
          <li>• Ward pages are accessible at <code className="text-gray-400">/ward/ward-1</code> through <code className="text-gray-400">/ward/ward-10</code></li>
          <li>• Subscribers sign up via these pages - you own the email data</li>
          <li>• Councillors see their subscriber count but NOT the actual emails</li>
          <li>• Custom content overrides the default templates</li>
        </ul>
      </div>
    </div>
  );
};

export default WardsTab;
